import React from 'react';
import DomainSelection from './DomainSelection';
import { ATL_SELECTION_TYPE } from '../../shared/constants';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { AtlSelectionList } from './AtlSelectionList';
import { AtlPreferencesMapper } from '../../../mappers/AtlPreferencesMapper';
import MotionSection from '@shared/components/animations/MotionSection';
import { AnimatePresence } from 'framer-motion';
import { OBSERVATION_TYPE } from '../../../../AppraiserForm/constants/observationType';
import { CURRENT_APPRAISER_ROLE } from '@shared/constants/currentAppraiserRole';

const SELECTION_FIELDS = {
  ATL: 'atl',
  ATL_OBSERVER: 'atlObserver'
};

const OBSERVABLE_ATL_APPRAISER_ROLES = [
  CURRENT_APPRAISER_ROLE.ATL_APPRENTICE,
  CURRENT_APPRAISER_ROLE.ATL_CANDIDATE
];

/**
 * @typedef {object} AtlSelectionProps
 * @property {Array<*>} [atlList]
 * @property {Object} [eventFormData]
 * @property {(selections: Object | ((prev: Object) => Object)) => void} [setSelections]
 * @property {boolean} [availableAtlsLoading]
 * @property {boolean} [loading]
 * @property {boolean} [isEditDisabled]
 * @property {number} [eventATLExternalId]
 * @property {number} [eventATLObserverExternalId]
 * @property {number} [selectionType]
 * @property {Object} [validationErrors]
 */

/**
 * @typedef {object} AtlSelectionComponentEntry
 * @property {React.ReactNode} [title]
 * @property {React.ReactNode} [component]
 */

/**
 * @param {AtlSelectionProps} props
 */
const AtlSelection = props => {
  const {
    atlList,
    eventFormData,
    setSelections,
    loading,
    availableAtlsLoading,
    eventATLObserverExternalId,
    isEditDisabled,
    eventATLExternalId = 0,
    validationErrors = { atl: false, domains: false },
    selectionType = ATL_SELECTION_TYPE.ATL
  } = props;

  const {
    domains: selectedDomains,
    atl: selectedAtl,
    atlObserver: selectedAtlObserver
  } = eventFormData;
  const { atl: hasAtlError, domains: hasDomainsError } = validationErrors;

  const showAtlObserverSelector =
    OBSERVABLE_ATL_APPRAISER_ROLES.includes(selectedAtl?.currentRole) ||
    (!selectedAtl && selectedAtlObserver);

  const availableAtls = getAvailableAtls(
    atlList,
    selectedDomains,
    eventATLExternalId,
    selectedAtlObserver?.value
  );

  const availableAtlObservers = getAvailableAtls(
    atlList,
    selectedDomains,
    eventATLObserverExternalId,
    null,
    isValidAtlObserverFn
  );

  const handleDomainSelection = domains => {
    handleDomainMismatch(SELECTION_FIELDS.ATL, selectedAtl, eventATLExternalId, domains);
    handleDomainMismatch(
      SELECTION_FIELDS.ATL_OBSERVER,
      selectedAtlObserver,
      eventATLObserverExternalId,
      domains
    );

    setSelections(prevState => ({
      ...prevState,
      domains: domains,
      atlPreferences:
        selectionType === ATL_SELECTION_TYPE.ATL_PREFERENCES
          ? getAvailableAtls(atlList, selectedDomains, eventATLExternalId).map(
              AtlPreferencesMapper.toAtlPreferencesFormData
            )
          : null
    }));
  };

  const handleDomainMismatch = (selectionType, selectedUser, eventStoredUserId, domains) => {
    const user = getAtlByExternalUserId(atlList, selectedUser?.value);

    if (hasUnmatchedDomain(domains, user) && selectedUser.value !== eventStoredUserId) {
      handleSelection(selectionType, null);
    }
  };

  const handleAtlSelection = value => {
    handleSelection(SELECTION_FIELDS.ATL, value);

    if (selectedAtlObserver && value?.currentRole === CURRENT_APPRAISER_ROLE.ATL) {
      handleAtlObserverSelection(null);
    }
  };

  const handleAtlObserverSelection = value => {
    handleSelection(SELECTION_FIELDS.ATL_OBSERVER, value);
  };

  const handleSelection = (selectionType, value) => {
    setSelections(prevState => ({ ...prevState, [selectionType]: value }));
  };

  const atlAssignmentComponentEntry = {
    title: 'ATL Assignment',
    component: (
      <AtlSelectionList
        id='atl-selector'
        placeholder='Select ATL'
        availableAtls={availableAtls}
        selectedAtl={selectedAtl}
        onSelectHandler={handleAtlSelection}
        loading={loading || availableAtlsLoading}
        hasError={hasAtlError}
      />
    )
  };

  /** @type {Array<[number, AtlSelectionComponentEntry]>} */
  const atlSelectionComponentEntries = [
    [ATL_SELECTION_TYPE.TBD, atlAssignmentComponentEntry],
    [ATL_SELECTION_TYPE.ATL, atlAssignmentComponentEntry],
    [ATL_SELECTION_TYPE.ATL_WITH_OBSERVER, atlAssignmentComponentEntry],
    [
      ATL_SELECTION_TYPE.VIEW_ATL,
      {
        title: 'ATL Assignment',
        component: (
          <AtlSelectionList
            id='atl-selector'
            placeholder='Select ATL'
            selectedAtl={selectedAtl}
            loading={loading}
            disabled={true}
          />
        )
      }
    ],
    [
      ATL_SELECTION_TYPE.VIEW_ATL_WITH_OBSERVER,
      {
        title: 'ATL Assignment',
        component: (
          <AtlSelectionList
            id='atl-selector'
            placeholder='Select ATL'
            selectedAtl={selectedAtl}
            loading={loading}
            disabled={true}
          />
        )
      }
    ],
    [
      ATL_SELECTION_TYPE.ATL_PREFERENCES,
      {
        title: (
          <div>
            <div>Select Domain(s) then</div>
            <div>ATL Preference(s)</div>
          </div>
        ),
        component: null
      }
    ]
  ];
  const atlSelectionComponentMap = new Map(atlSelectionComponentEntries);

  const atlObserverSelectionComponentMap = new Map([
    [
      ATL_SELECTION_TYPE.ATL_WITH_OBSERVER,
      {
        component: showAtlObserverSelector && (
          <MotionSection>
            <AtlSelectionList
              id='atl-observer-selector'
              placeholder='Select ATL Observer'
              availableAtls={availableAtlObservers}
              selectedAtl={selectedAtlObserver}
              onSelectHandler={handleAtlObserverSelection}
            />
          </MotionSection>
        )
      }
    ],
    [
      ATL_SELECTION_TYPE.VIEW_ATL_WITH_OBSERVER,
      {
        component: (
          <AtlSelectionList
            id='atl-observer-selector'
            placeholder='Select ATL Observer'
            selectedAtl={selectedAtlObserver}
            disabled={true}
          />
        )
      }
    ]
  ]);

  return (
    <section className='page-section'>
      <div className='section-title bold'>
        <SkeletonWrapper show={selectionType === ATL_SELECTION_TYPE.TBD} inheritChildHeight>
          <span>{atlSelectionComponentMap.get(selectionType)?.title}</span>
        </SkeletonWrapper>
      </div>
      <DomainSelection
        hasError={hasDomainsError}
        selectedDomains={selectedDomains}
        handleDomainSelection={handleDomainSelection}
        loading={loading}
        disabled={isEditDisabled}
      />
      <SkeletonWrapper show={selectionType === ATL_SELECTION_TYPE.TBD}>
        {atlSelectionComponentMap.get(selectionType)?.component}
      </SkeletonWrapper>

      <AnimatePresence mode='wait'>
        {atlObserverSelectionComponentMap.get(selectionType)?.component}
      </AnimatePresence>
    </section>
  );
};

const getAvailableAtls = (
  atls,
  domains,
  storedEventUserExternalId,
  excludedUserId,
  isValidAtlObserverFn
) =>
  atls.filter(atl => {
    if (atl.ExternalUserId === excludedUserId) {
      return false;
    }

    if (atl.ExternalUserId === storedEventUserExternalId) {
      return true;
    }

    const observerFilter = isValidAtlObserverFn ? isValidAtlObserverFn(atl) : true;

    return observerFilter && domains.every(domain => hasExperienceForDomain(atl, domain));
  });

const isValidAtlObserverFn = atl =>
  atl.CurrentRole.Value === CURRENT_APPRAISER_ROLE.ATL &&
  atl.Observation === OBSERVATION_TYPE.ATL_OBSERVER;

const getAtlByExternalUserId = (atlList, externalUserId) =>
  atlList.find(atl => externalUserId && atl.ExternalUserId === externalUserId);

const hasUnmatchedDomain = (domains, atl) =>
  atl && domains.some(domain => !hasExperienceForDomain(atl, domain));

const hasExperienceForDomain = (atl, domain) => {
  return atl.DomainExperiences.some(experience => experience.Domain === domain);
};

export default AtlSelection;
